/**
 * Die Status, die ein Ortsverband selbst festlegt (ADR 0005).
 *
 * Ein Status beschreibt, **in welchem Zustand** ein Artikel ist -- nicht, wo er
 * ist. Name und Farbe sind frei. Zwei Kennzeichen tragen die Bedeutung, auf die
 * sich Zaehlungen und Module verlassen:
 *
 * - `einsatzbereit`: Der Artikel taugt fuer den Einsatz. Nur solche zaehlen
 *   zum verfuegbaren Bestand.
 * - `archiviert`: Der Artikel faellt aus allen aktiven Listen (KONZEPT.md §7.2,
 *   etwa verloren oder gestohlen). Taucht er wieder auf, genuegt eine
 *   Einbuchung -- dann gilt wieder der Standardstatus.
 *
 * Genau ein Status ist der **Standard**: der, den ein Artikel hat, bevor ihm
 * jemand einen anderen gesetzt hat.
 */
import { z } from "zod";
import { KurzText } from "./basis.js";
import { Status } from "./enums.js";
/**
 * Farbe als sechsstelliger Hexwert in Kleinbuchstaben, etwa `#16a34a` -- so,
 * wie ein Farbwaehler des Browsers ihn liefert.
 */
export const Farbe = z.string().regex(/^#[0-9a-f]{6}$/, "Farbe als Hexwert wie #16a34a");
export const StatusArt = z.object({
    schluessel: Status,
    bezeichnung: KurzText,
    farbe: Farbe,
    einsatzbereit: z.boolean(),
    archiviert: z.boolean(),
    /**
     * Wer diesen Status bekommt, kommt nach „Unbekannt“ (ADR 0020): Der Core
     * bucht ihn dorthin, und er verschwindet aus seinem Packstück. Nur bei
     * archivierenden Status, voreingestellt für verloren und gestohlen. Seit
     * Vertrag 2.9; eine lokale Kopie von vorher kennt das Feld nicht.
     */
    ort_unbekannt: z.boolean(),
    /**
     * Nur ein Administrator darf diesen Status setzen (ADR 0025), und nur mit
     * Netz. Der Server prueft die Rolle des Uebertragenden. Der Standardstatus
     * kann es nicht sein. Seit Vertrag 2.10, wie die beiden folgenden Felder:
     * Eine lokale Kopie von vorher kennt sie nicht, dort gelten sie als aus.
     */
    nur_admin: z.boolean(),
    /** Vor dem Setzen muss man ein zweites Mal tippen, ohne Popup (ADR 0025). */
    mit_bestaetigung: z.boolean(),
    /**
     * „Alles da“ und „Wieder da“ lassen solche Geraete liegen, statt sie an
     * ihren Stammplatz zu buchen (ADR 0025), etwa defekt oder in Pruefung.
     */
    rueckbuchung_ignorieren: z.boolean(),
    standard: z.boolean(),
    /** Reihenfolge in Auswahllisten und Diagrammen, aufsteigend. */
    reihenfolge: z.number().int(),
    /**
     * Stillgelegte Status lassen sich nicht mehr setzen, bleiben aber lesbar:
     * Alte Buchungen tragen sie weiter.
     */
    aktiv: z.boolean(),
});
/** Einen Status anlegen. Der Standard wird ueber `StatusArtAendern` gewechselt. */
export const StatusArtAnlegen = z
    .object({
    schluessel: Status,
    bezeichnung: KurzText,
    farbe: Farbe,
    einsatzbereit: z.boolean().default(false),
    archiviert: z.boolean().default(false),
    ort_unbekannt: z.boolean().default(false),
    nur_admin: z.boolean().default(false),
    mit_bestaetigung: z.boolean().default(false),
    rueckbuchung_ignorieren: z.boolean().default(false),
    reihenfolge: z.number().int().default(100),
})
    .refine((s) => !(s.einsatzbereit && s.archiviert), {
    message: "Ein archivierter Status kann nicht einsatzbereit sein",
    path: ["archiviert"],
})
    .refine((s) => !s.ort_unbekannt || s.archiviert, {
    message: "Nach „Unbekannt“ führt nur ein archivierender Status",
    path: ["ort_unbekannt"],
});
/**
 * Einen Status aendern. Der Schluessel bleibt, alles andere ist frei.
 *
 * `standard: true` macht diesen Status zum Standard und nimmt es dem
 * bisherigen. `standard: false` gibt es nicht: Irgendein Status muss es sein.
 * Ob der Standard `nur_admin` traegt, weiss erst der Server, der den alten
 * Stand kennt.
 */
export const StatusArtAendern = z
    .object({
    bezeichnung: KurzText.optional(),
    farbe: Farbe.optional(),
    einsatzbereit: z.boolean().optional(),
    archiviert: z.boolean().optional(),
    ort_unbekannt: z.boolean().optional(),
    nur_admin: z.boolean().optional(),
    mit_bestaetigung: z.boolean().optional(),
    rueckbuchung_ignorieren: z.boolean().optional(),
    reihenfolge: z.number().int().optional(),
    aktiv: z.boolean().optional(),
    standard: z.literal(true).optional(),
})
    .refine((s) => !(s.einsatzbereit === true && s.archiviert === true), {
    message: "Ein archivierter Status kann nicht einsatzbereit sein",
    path: ["archiviert"],
})
    .refine((s) => !(s.standard === true && s.nur_admin === true), {
    message: "Der Standardstatus kann nicht „Nur Admin“ sein",
    path: ["nur_admin"],
});
/**
 * Die Status, mit denen jede Installation beginnt.
 *
 * Die Schluessel sind die Werte, die bis Vertrag 1.x fest vorgegeben waren --
 * damit bleiben alle alten Buchungen gueltig. Neu ist nur `einsatzbereit`: Bis
 * dahin stand dafuer `im_lager`, und das war ein Ort, kein Zustand.
 *
 * Dieselbe Liste legt die Migration `0003_status` an. Ein Test prueft, dass
 * beide uebereinstimmen.
 */
export const STANDARD_STATUSARTEN = [
    {
        schluessel: "einsatzbereit",
        bezeichnung: "Einsatzbereit",
        farbe: "#16a34a",
        einsatzbereit: true,
        archiviert: false,
        ort_unbekannt: false,
        nur_admin: false,
        mit_bestaetigung: false,
        rueckbuchung_ignorieren: false,
        standard: true,
        reihenfolge: 10,
        aktiv: true,
    },
    {
        schluessel: "in_pruefung",
        bezeichnung: "In Prüfung",
        farbe: "#ea580c",
        einsatzbereit: false,
        archiviert: false,
        ort_unbekannt: false,
        nur_admin: false,
        mit_bestaetigung: false,
        rueckbuchung_ignorieren: true,
        standard: false,
        reihenfolge: 20,
        aktiv: true,
    },
    {
        schluessel: "defekt",
        bezeichnung: "Defekt",
        farbe: "#dc2626",
        einsatzbereit: false,
        archiviert: false,
        ort_unbekannt: false,
        nur_admin: false,
        mit_bestaetigung: false,
        rueckbuchung_ignorieren: true,
        standard: false,
        reihenfolge: 30,
        aktiv: true,
    },
    {
        schluessel: "verloren",
        bezeichnung: "Verloren",
        farbe: "#7c3aed",
        einsatzbereit: false,
        archiviert: true,
        ort_unbekannt: true,
        nur_admin: true,
        mit_bestaetigung: true,
        rueckbuchung_ignorieren: false,
        standard: false,
        reihenfolge: 40,
        aktiv: true,
    },
    {
        schluessel: "gestohlen",
        bezeichnung: "Gestohlen",
        farbe: "#9f1239",
        einsatzbereit: false,
        archiviert: true,
        ort_unbekannt: true,
        nur_admin: true,
        mit_bestaetigung: true,
        rueckbuchung_ignorieren: false,
        standard: false,
        reihenfolge: 50,
        aktiv: true,
    },
    {
        // Nicht archivierend: KONZEPT.md §7.2 nennt dafuer nur verloren und
        // gestohlen. Wer ausgesondertes Material aus den Listen haben will, stellt
        // es in den Stammdaten um.
        schluessel: "ausgebucht",
        bezeichnung: "Ausgesondert",
        farbe: "#737373",
        einsatzbereit: false,
        archiviert: false,
        ort_unbekannt: false,
        nur_admin: true,
        mit_bestaetigung: true,
        rueckbuchung_ignorieren: false,
        standard: false,
        reihenfolge: 60,
        aktiv: true,
    },
    {
        schluessel: "archiviert",
        bezeichnung: "Archiviert",
        farbe: "#a3a3a3",
        einsatzbereit: false,
        archiviert: true,
        ort_unbekannt: false,
        nur_admin: true,
        mit_bestaetigung: true,
        rueckbuchung_ignorieren: false,
        standard: false,
        reihenfolge: 70,
        aktiv: true,
    },
];
//# sourceMappingURL=statusart.js.map